import { useState, useEffect } from 'react';
import { GridSortModel } from '@mui/x-data-grid';
import { BuyerCustomFilter, RepSelection, BudgetRangeEnum } from '@common/enums/BuyerCustomFilter.ts';

const STORAGE_KEY = 'adminBuyersFilters';

export interface PersistedBuyersFilters {
    search: string;
    sortModel: GridSortModel;
    customFilter: BuyerCustomFilter;
    repSelection: RepSelection;
    budgetRange: BudgetRangeEnum;
    page: number;
    includePending: boolean;
}

const DEFAULT_FILTERS: PersistedBuyersFilters = {
    search: '',
    sortModel: [{ field: 'name', sort: 'asc' }],
    customFilter: BuyerCustomFilter.All,
    repSelection: null,
    budgetRange: BudgetRangeEnum.SinceLastTransaction,
    page: 1,
    includePending: true,
};

/**
 * Custom hook to persist buyers table filters in localStorage
 * Filters are automatically saved on change and cleared on logout
 */
export function usePersistedBuyersFilters(isForStatic: boolean) {
    // Load persisted filters on mount
    const loadFilters = (): PersistedBuyersFilters => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const parsed = JSON.parse(stored) as PersistedBuyersFilters;
                // Override customFilter default for static subscriptions
                if (isForStatic && parsed.customFilter === BuyerCustomFilter.All) {
                    parsed.customFilter = BuyerCustomFilter.Active;
                }
                return parsed;
            }
        } catch (error) {
            console.error('Error loading persisted filters:', error);
        }
        // Return defaults with customFilter adjusted for static
        return {
            ...DEFAULT_FILTERS,
            customFilter: isForStatic ? BuyerCustomFilter.Active : BuyerCustomFilter.All,
        };
    };

    const [filters, setFiltersState] = useState<PersistedBuyersFilters>(loadFilters);

    // Save filters to localStorage whenever they change
    useEffect(() => {
        try {
            // Check if localStorage was externally cleared (e.g., by logout)
            // If it was cleared and our state doesn't match defaults, reset state
            const stored = localStorage.getItem(STORAGE_KEY);
            if (!stored) {
                const defaults = {
                    ...DEFAULT_FILTERS,
                    customFilter: isForStatic ? BuyerCustomFilter.Active : BuyerCustomFilter.All,
                };
                // Only reset if current state differs from defaults
                if (JSON.stringify(filters) !== JSON.stringify(defaults)) {
                    setFiltersState(defaults);
                    return; // Don't save, we just reset
                }
            }

            localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
        } catch (error) {
            console.error('Error saving filters to localStorage:', error);
        }
    }, [filters, isForStatic]);

    // Setter function to update filters
    const setFilters = (updates: Partial<PersistedBuyersFilters>) => {
        setFiltersState((prev) => ({ ...prev, ...updates }));
    };

    return { filters, setFilters };
}

/**
 * Clear persisted buyers filters from localStorage
 * This should be called on logout
 */
export function clearPersistedBuyersFilters(): void {
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
        console.error('Error clearing persisted filters:', error);
    }
}
